import React, { Component } from 'react'
import type { ReactNode } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ClerkProvider } from '@clerk/clerk-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import './index.css'

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string
if (!publishableKey) throw new Error('VITE_CLERK_PUBLISHABLE_KEY is not set')

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 10_000 } },
})

class RootErrorBoundary extends Component<{ children: ReactNode }, { err: string | null }> {
  state = { err: null }
  static getDerivedStateFromError(e: Error) { return { err: e.message + '\n' + e.stack } }
  componentDidCatch(e: Error, info: React.ErrorInfo) { console.error('[RootErrorBoundary]', e, info) }
  render() {
    if (this.state.err) return (
      <div style={{ position: 'fixed', inset: 0, background: '#0a0010', color: '#f87171', padding: 32, fontFamily: 'monospace', fontSize: 12, overflowY: 'auto', zIndex: 9999 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, color: '#fca5a5' }}>Application crash — copy and report:</div>
        <div style={{ marginBottom: 16, color: '#9ca3af', fontSize: 11 }}>This error prevented the UI from rendering. Open DevTools Console for more detail.</div>
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6 }}>{this.state.err}</pre>
      </div>
    )
    return this.props.children
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ClerkProvider publishableKey={publishableKey}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ClerkProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  </React.StrictMode>,
)
